import { useState, useEffect } from 'react';
import { devicesApi, Device } from '../../api/devices.api';
import Table, { Column } from '../../components/common/Table';
import Loading from '../../components/common/Loading';
import ImageViewerModal from '../../components/common/ImageViewerModal';

const ScrappedList = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    fetchScrappedDevices();
  }, []);

  const fetchScrappedDevices = async () => {
    try {
      setIsLoading(true);
      const scrapped = await devicesApi.getScrapped();
      setDevices(scrapped);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Hurdalar yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenImageViewer = (images: string[], index: number = 0) => {
    setSelectedImages(images);
    setSelectedImageIndex(index);
    setIsImageViewerOpen(true);
  };

  const getProductName = (product: any): string => {
    if (typeof product === 'object' && product !== null) {
      return product.name || '-';
    }
    return '-';
  };

  const getCreatedByName = (createdBy: any): string => {
    if (typeof createdBy === 'object' && createdBy !== null) {
      if (createdBy.firstName && createdBy.lastName) {
        return `${createdBy.firstName} ${createdBy.lastName}`;
      }
      return createdBy.email || createdBy.username || '-';
    }
    return '-';
  };

  const columns: Column<Device>[] = [
    {
      key: 'serialNumber',
      header: 'Seri No',
      render: (item) => {
        if (!item.serialNumber) return '-';
        const isMongoId = /^[a-f\d]{24}$/i.test(item.serialNumber);
        return isMongoId ? (
          <span className="text-xs text-gray-500" title="Otomatik oluşturuldu">
            {item.serialNumber.substring(0, 8)}...
          </span>
        ) : (
          item.serialNumber
        );
      },
    },
    { key: 'product', header: 'Ürün', render: (item) => getProductName(item.product) },
    { key: 'assetTag', header: 'Varlık Etiketi', render: (item) => item.assetTag || '-' },
    {
      key: 'currentLocation',
      header: 'Hurda Tarihi',
      render: (item) => {
        if (item.currentLocation?.since) {
          return new Date(item.currentLocation.since).toLocaleDateString('tr-TR');
        }
        return '-';
      },
    },
    {
      key: 'createdBy',
      header: 'İşlemi Yapan',
      render: (item) => getCreatedByName(item.createdBy),
    },
    {
      key: 'scrapPhotos',
      header: 'Fotoğraflar',
      render: (item) => (
        item.scrapPhotos && item.scrapPhotos.length > 0 ? (
          <div className="flex gap-1">
            {item.scrapPhotos.slice(0, 3).map((photo, index) => (
              <img
                key={index}
                src={photo}
                alt={`Fotoğraf ${index + 1}`}
                className="w-12 h-12 object-cover rounded border border-gray-300 cursor-pointer hover:scale-110 transition-transform"
                onClick={() => handleOpenImageViewer(item.scrapPhotos!, index)}
                title="Görüntülemek için tıklayın"
              />
            ))}
            {item.scrapPhotos.length > 3 && (
              <div 
                className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded border border-gray-300 text-xs text-gray-600 cursor-pointer hover:bg-gray-200 transition-colors"
                onClick={() => handleOpenImageViewer(item.scrapPhotos!, 3)}
                title="Tüm fotoğrafları görüntüle"
              >
                +{item.scrapPhotos.length - 3}
              </div>
            )}
          </div>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )
      ),
    },
  ];

  if (isLoading) {
    return <Loading text="Hurdalar yükleniyor..." />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Hurdalar</h1>
        <div className="text-sm text-gray-600">
          Toplam: <span className="font-semibold">{devices.length}</span> hurda cihaz
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <Table data={devices} columns={columns} emptyMessage="Henüz hurda cihaz bulunmamaktadır" />

      {/* Image Viewer Modal */}
      <ImageViewerModal
        isOpen={isImageViewerOpen}
        onClose={() => setIsImageViewerOpen(false)}
        images={selectedImages}
        initialIndex={selectedImageIndex}
      />
    </div>
  );
};

export default ScrappedList;
